import React, { FC, useCallback, useMemo } from 'react';

import { useFormikContext } from 'formik';

import BasicButton from '../../../../../../components/buttons/basicButton';
import AampeTooltip from '../../../../../../components/shared/AampeTooltip/AampeTooltip';
import styled from 'styled-components';

import aampeTheme from '../../../../../../constants/aampeTheme';
import { ButtonType } from './constants';

import { isIosButtonValidFilled } from './helpers';

import { NotificationButtonFormValues } from '../../../../../../models/formula.model';

interface NotificationActionButtonProps {
    currentIndex: number;
    setCurrentIndex: (val: number) => void;
    handleClose?: () => void;
    handleSubmit: (values: NotificationButtonFormValues) => void;
    isUpdating: boolean;
}

const NotificationActionButton: FC<NotificationActionButtonProps> = ({
    currentIndex,
    setCurrentIndex,
    handleClose,
    handleSubmit,
    isUpdating,
}) => {
    const { values, isValid, dirty } =
        useFormikContext<NotificationButtonFormValues>();

    const { androidToggle, iosToggle, iosNotificationCategory, iosButtons } =
        values;

    const buttonName = useMemo<string>(() => {
        if (currentIndex === 0) {
            return 'Next';
        }
        return isUpdating ? 'Update buttons' : 'Add buttons';
    }, [currentIndex, isUpdating]);

    const onClick = useCallback(() => {
        if (currentIndex === 0) {
            setCurrentIndex(currentIndex + 1);
        } else {
            handleSubmit(values);
        }
    }, [currentIndex, handleSubmit, setCurrentIndex, values]);

    const isDisabled = useMemo<boolean>(
        () =>
            currentIndex === 0
                ? iosToggle
                    ? !iosNotificationCategory ||
                      !isIosButtonValidFilled(iosButtons)
                    : false
                : androidToggle
                ? !isValid || !dirty
                : false,
        [
            androidToggle,
            currentIndex,
            dirty,
            iosButtons,
            iosNotificationCategory,
            iosToggle,
            isValid,
        ],
    );

    const buttonTitle = useMemo<string>(
        () =>
            isDisabled
                ? `Complete the ${
                      currentIndex === 0 ? 'iOS' : 'Android'
                  } button setup or disable it to continue`
                : '',
        [currentIndex, isDisabled],
    );
    return (
        <Container>
            <ButtonContainer>
                <StyledButton
                    isPrimary={false}
                    name="Cancel"
                    action={handleClose}
                />
            </ButtonContainer>
            <AampeTooltip title={buttonTitle} arrow placement="top">
                <ButtonContainer>
                    <StyledButton
                        isPrimary
                        name={buttonName}
                        type={ButtonType.BUTTON}
                        isDisabled={isDisabled}
                        action={onClick}
                    />
                </ButtonContainer>
            </AampeTooltip>
        </Container>
    );
};

const StyledButton = styled(BasicButton)({
    borderRadius: aampeTheme.borderRadius.default,
    fontWeight: 500,
    width: '100%',
    fontSize: aampeTheme.size.font.default,
    lineHeight: '18px',
    padding: aampeTheme.spacing4(3, 11),
});

const ButtonContainer = styled.div({
    width: '100%',
});

const Container = styled.div({
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    gap: aampeTheme.spacing4(4),
    paddingTop: aampeTheme.spacing4(4),
});

export default NotificationActionButton;
